import { createSlice } from '@reduxjs/toolkit'
import { RootState, AppThunk } from './store'
import { get, post } from 'utils/rest'
import * as Toast from 'components/Toast'

export interface AuthState {
  isAuthenticated: boolean
  isAuthenticating: boolean
  isLoading: boolean
  roles: any[] // eslint-disable-line
  name: string
  user: User
  hasError: boolean
  sessionExpiryWarning: boolean
}

export interface User {
  id: string
  email: string
  userName: string
  userRoles: string[]
  token: string
  claims?: Claim[]
}

interface Claim {
  type: string
  value: string
}

const writeState = (token: string) => {
  localStorage.setItem('token', JSON.stringify(token))
}

const clearState = () => {
  localStorage.removeItem('token')
}

const createInitialState = (): AuthState => {
  return {
    isAuthenticated: false,
    isAuthenticating: false,
    isLoading: true,
    name: '',
    roles: [],
    user: {
      id: '',
      token: '',
      email: '',
      userName: '',
      userRoles: [],
    },
    hasError: false,
    sessionExpiryWarning: false,
  }
}

export const authSlice = createSlice({
  name: 'auth',
  initialState: createInitialState(),
  reducers: {
    loginBegin: (state) => {
      state.isLoading = true
      state.isAuthenticating = true
    },
    loginSuccess: (state, resJson) => {
      state.isAuthenticated = true
      state.isAuthenticating = false
      state.isLoading = false
      state.user = resJson.payload
      state.hasError = false
      state.sessionExpiryWarning = false
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false
      state.sessionExpiryWarning = false
      clearState()
    },
    loginFailure: (state) => {
      state.isLoading = false
      state.isAuthenticating = false
      state.isAuthenticated = false
      state.hasError = true
    },
    identificationSuccess: (state, resJson) => {
      state.isAuthenticated = true
      state.isAuthenticating = false
      state.isLoading = false
      state.user = resJson.payload
      state.sessionExpiryWarning = false
    },
    identificationFailure: (state) => {
      state.isLoading = false
      state.isAuthenticating = false
      state.isAuthenticated = false
    },
    showSessionExpiryWarning: (state) => {
      state.sessionExpiryWarning = true
    },
    dismissSessionExpiryWarning: (state) => {
      state.sessionExpiryWarning = false
    },
  },
})

export const {
  loginBegin,
  loginSuccess,
  loginFailure,
  logoutSuccess,
  identificationSuccess,
  identificationFailure,
  showSessionExpiryWarning,
  dismissSessionExpiryWarning,
} = authSlice.actions

export interface Credentials {
  userName: string
  password: string
}

export const loginAsync =
  (userName: string, pwd: string): AppThunk =>
  async (dispatch) => {
    dispatch(loginBegin())
    try {
      const credentials: Credentials = { userName: userName, password: pwd }
      const response = await post<User>(`/api/authentication/login`, credentials)

      if (response.status === 200 && response.parsedBody?.data) {
        writeState(response.parsedBody?.data.token)
        dispatch(loginSuccess(response.parsedBody.data))
      } else {
        Toast.dispatchToastError({ message: response.parsedBody?.message ?? 'Fel användarnamn eller lösenord' })
        dispatch(loginFailure())
      }
    } catch (err) {
      dispatch(loginFailure())
    }
  }

export const logoutAsync = (): AppThunk => async (dispatch) => {
  dispatch(logoutSuccess())
  window.location.replace(`${window.location.origin}`)
}

export const renewSessionAsync = (): AppThunk => async (dispatch) => {
  try {
    const response = await post<User>(`/api/authentication/renew`, {})
    if (response.status === 200 && response.parsedBody?.data) {
      writeState(response.parsedBody.data.token)
      dispatch(loginSuccess(response.parsedBody.data))
    } else {
      dispatch(logoutAsync())
    }
  } catch {
    dispatch(logoutAsync())
  }
}

export const loadSessionAsync = (): AppThunk => async (dispatch) => {
  if (!localStorage.getItem('token')) {
    return
  }

  try {
    const response = await get<User>(`/api/authentication/me`)
    if (response.status === 200 && response.parsedBody?.data) {
      dispatch(identificationSuccess(response.parsedBody?.data))
    } else {
      dispatch(identificationFailure())
    }
  } catch (e) {
    dispatch(identificationFailure())
  }
}

export const selectAuthState = (state: RootState): AuthState => state.auth
export const selectAuthIsLoading = (state: RootState): boolean => state.auth.isLoading
export const selectUserIsAuthenticated = (state: RootState): boolean => state.auth.isAuthenticated
export const selectUser = (state: RootState): User => state.auth.user
export const selectSessionExpiryWarning = (state: RootState): boolean => state.auth.sessionExpiryWarning

export default authSlice.reducer
